import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的内容不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Organization', name: article.author },
        datePublished: article.date,
        url: `https://www.madouzx.com/article/${article.slug}`,
        publisher: { '@type': 'Organization', name: '麻豆在线' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.madouzx.com/' },
          { '@type': 'ListItem', position: 2, name: category?.name || '分类', item: `https://www.madouzx.com/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${article.title} - 麻豆在线`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.madouzx.com/article/${article.slug}`}
        ogType="article"
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类', href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime} 阅读
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-foreground-900 prose-li:text-foreground-700 prose-a:text-primary-600 prose-h2:text-xl md:prose-h2:text-2xl"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 mb-2">
                        {ra.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span>{ra.date}</span>
                        <span>{ra.readTime} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}